import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import {useEffect, useState} from 'react';

/**
 * useAgentAliases fetches the agent variants a client may request via
 * `agent_config_override` from the wb_agent service (GET /agent-aliases),
 * returning them sorted by name as the service provides them.
 *
 * Pass `enabled: false` to skip the fetch entirely — the agent override
 * selector is internal-only, so non-internal users never hit the endpoint.
 * The list is best-effort: the selector still accepts free text, so a failed
 * or empty fetch simply means no suggestions.
 */
export function useAgentAliases(enabled: boolean): string[] {
  const client = useWbAgentClient();
  const [aliases, setAliases] = useState<string[]>([]);

  useEffect(() => {
    if (!enabled || !client) {
      return undefined;
    }

    const controller = new AbortController();
    client
      .get('/agent-aliases', undefined, controller.signal)
      .then(res => {
        if (!controller.signal.aborted) {
          setAliases(res.aliases);
        }
      })
      .catch(() => {
        // Suggestions are best-effort hints; the picker still accepts free
        // text, so a failed fetch silently falls back to no suggestions.
      });

    return () => controller.abort();
  }, [enabled, client]);

  return aliases;
}
